/**
 * Account — signed-in user, password, two-factor authentication and sign-out.
 *
 * Not a screen of its own in the design; it uses the same card language as
 * Settings / Profile (weco2/settings.jsx, weco2/profile.jsx).
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KeyRound, LogOut, Shield, type LucideIcon } from "lucide-react";
import { UserAvatar } from "@weco/suite-chrome";
import { useShellStore } from "../store";
import { fetchMe } from "../api/preferences.api";
import { ChangePasswordForm } from "../components/ChangePasswordForm";
import { TwoFactorSettings } from "../components/TwoFactorSettings";

const CARD = "bg-background border border-border p-6 max-md:p-4 rounded-3xl shadow-sm";

function CardHeading({ icon: Icon, title, hint }: { icon: LucideIcon; title: string; hint: string }) {
  return (
    <div className="flex items-center gap-3 mb-6 text-start">
      <div className="w-10 h-10 bg-primary/10 text-primary rounded-xl flex items-center justify-center shrink-0">
        <Icon className="size-5" aria-hidden />
      </div>
      <div className="min-w-0">
        <h2 className="text-lg font-bold">{title}</h2>
        <p className="text-sm text-muted-foreground">{hint}</p>
      </div>
    </div>
  );
}

export const AccountPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useShellStore();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const [orgName, setOrgName] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (!cancelled) setOrgName(me?.organization?.name ?? null);
      })
      .catch(() => {
        /* the org line is optional */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto no-scrollbar p-6 max-md:p-4 lg:p-12 bg-muted/10 h-full">
      <div className="max-w-3xl mx-auto w-full mt-4 lg:mt-8 space-y-8 max-md:space-y-5 pb-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="text-start">
            <h1 className="text-3xl max-md:text-[22px] lg:text-4xl font-black tracking-tight">
              {t("account.title", { defaultValue: "Account" })}
            </h1>
            <p className="text-muted-foreground font-medium mt-2 max-md:text-[12.5px]">
              {t("account.subtitle", { defaultValue: "Your profile and session" })}
            </p>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            className="self-start sm:self-auto inline-flex items-center gap-2 px-5 h-11 rounded-2xl border border-border text-sm font-bold text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
          >
            <LogOut className="size-4 rtl:-scale-x-100" aria-hidden />
            {signingOut
              ? t("account.signingOut", { defaultValue: "Signing out…" })
              : t("account.signOut", { defaultValue: "Sign out" })}
          </button>
        </div>

        <section className={CARD}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <UserAvatar name={user?.name ?? "User"} avatarUrl={user?.avatarUrl} sizePx={64} />
            <div className="min-w-0 flex-1 text-start">
              <p className="text-lg font-bold truncate">{user?.name || "-"}</p>
              <p className="text-sm text-muted-foreground truncate" dir="ltr">
                {user?.email ?? "-"}
              </p>
              {orgName && <p className="text-xs text-muted-foreground mt-0.5 truncate">{orgName}</p>}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate("/profile")}
                className="px-5 py-3 bg-foreground text-background font-bold rounded-2xl text-sm hover:scale-[1.02] transition-transform"
              >
                {t("account.editProfile", { defaultValue: "Edit profile" })}
              </button>
              <button
                type="button"
                onClick={() => navigate("/preferences")}
                className="px-5 py-3 border border-border font-bold rounded-2xl text-sm hover:bg-muted transition-colors"
              >
                {t("account.preferences", { defaultValue: "Preferences" })}
              </button>
            </div>
          </div>
        </section>

        <section className={CARD}>
          <CardHeading
            icon={KeyRound}
            title={t("settings.account.passwordTitle")}
            hint={t("settings.account.passwordHint")}
          />
          <ChangePasswordForm />
        </section>

        <section className={CARD}>
          <CardHeading icon={Shield} title={t("settings.security.title")} hint={t("settings.security.hint")} />
          <TwoFactorSettings />
        </section>
      </div>
    </div>
  );
};

export default AccountPage;
